/**
 * Pages chargées à la demande (découpage du code par route).
 *
 * Les pages publiques (accueil, /c/:id, /pay/:token, /fete/:pageId,
 * /donate/:orgId, /organisations…) restent dans le fichier principal, importées
 * directement par App.js : un donateur qui arrive depuis WhatsApp reçoit sa
 * page en un seul téléchargement, sans le code de l'administration.
 *
 * Tout ce qui est ici n'est téléchargé qu'à l'ouverture d'une page d'équipe,
 * regroupé en trois morceaux (nom donné par webpackChunkName) :
 *
 *   admin  : cadre de l'application (barre latérale) + pages des synagogues
 *            (tableau de bord, donateurs, promesses, campagnes, Cerfa,
 *            messages, réglages…). Un seul morceau : une fois arrivé, on passe
 *            d'une page à l'autre instantanément, comme avant. Les grosses
 *            bibliothèques qui ne servent qu'ici (recharts, React Query) en
 *            dépendent seules et quittent donc le fichier principal.
 *   owner  : pages réservées à l'équipe Terouma (owner).
 *   poche  : Terouma Poche, la version téléphone (dictée du soir…).
 *   compte : accès de l'équipe des synagogues — connexion, mot de passe
 *            oublié, invitation, lien du responsable de la collecte. Pages
 *            sans connexion mais jamais ouvertes par un donateur.
 *
 * ⚠️ Une page publique ne doit JAMAIS importer un fichier de cette liste (ni
 * un composant qui n'existe que pour elles) : le code de l'administration
 * repasserait dans le fichier principal que chaque donateur télécharge.
 */
import { lazyAvecRecharge as differee } from "../lib/chargementDiffere";

// ── admin ────────────────────────────────────────────────────────────────
// Cadre (barre latérale) + fournisseur React Query, qui ne sert qu'ici.
export const CadreAdmin = differee(() => import(/* webpackChunkName: "admin" */ "../components/layout/CadreAdmin"));
export const Dashboard = differee(() => import(/* webpackChunkName: "admin" */ "../pages/Dashboard"));
export const GestionDonsPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/GestionDonsPage"));
export const DonorsListPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/DonorsListPage"));
export const DonorFormPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/DonorFormPage"));
export const DonorDetailPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/DonorDetailPage"));
export const PledgesPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/PledgesPage"));
export const PledgeFormPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/PledgeFormPage"));
export const PledgeDetailPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/PledgeDetailPage"));
export const CerfasPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/CerfasPage"));
export const PostShabbatPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/PostShabbatPage"));
export const KiddoushAdminPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/KiddoushAdminPage"));
export const CampaignsListPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/CampaignsListPage"));
export const CampaignNewPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/CampaignNewPage"));
export const CampaignDetailPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/CampaignDetailPage"));
export const CommunicationPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/CommunicationPage"));
export const MessagesPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/MessagesPage"));
export const WhatsAppInboxPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/WhatsAppInboxPage"));
export const WhatsAppStatsPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/WhatsAppStatsPage"));
export const SmsStatsPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/SmsStatsPage"));
export const SecurityPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/SecurityPage"));
export const UsersSettingsPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/UsersSettingsPage"));
export const OrganizationSettingsPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/OrganizationSettingsPage"));
export const OrganizationSubscriptionPage = differee(() => import(/* webpackChunkName: "admin" */ "../pages/OrganizationSubscriptionPage"));

// ── owner (équipe Terouma) ───────────────────────────────────────────────
export const OwnerDashboard = differee(() => import(/* webpackChunkName: "owner" */ "../pages/OwnerDashboard"));
export const OrganizationsListPage = differee(() => import(/* webpackChunkName: "owner" */ "../pages/OrganizationsListPage"));
export const OwnerOrgDashboard = differee(() => import(/* webpackChunkName: "owner" */ "../pages/OwnerOrgDashboard"));
export const OwnerAiUsagePage = differee(() => import(/* webpackChunkName: "owner" */ "../pages/OwnerAiUsagePage"));
export const OwnerConsumptionPage = differee(() => import(/* webpackChunkName: "owner" */ "../pages/OwnerConsumptionPage"));
export const OwnerPlatformSettingsPage = differee(() => import(/* webpackChunkName: "owner" */ "../pages/OwnerPlatformSettingsPage"));
export const OwnerRevenuePage = differee(() => import(/* webpackChunkName: "owner" */ "../pages/OwnerRevenuePage"));
export const OwnerSubscriptionsPage = differee(() => import(/* webpackChunkName: "owner" */ "../pages/OwnerSubscriptionsPage"));
export const OwnerStripePage = differee(() => import(/* webpackChunkName: "owner" */ "../pages/OwnerStripePage"));
export const SettingsPage = differee(() => import(/* webpackChunkName: "owner" */ "../pages/SettingsPage"));
export const OnboardingPage = differee(() => import(/* webpackChunkName: "owner" */ "../pages/OnboardingPage"));

// ── poche (version téléphone) ────────────────────────────────────────────
export const PocheShell = differee(() => import(/* webpackChunkName: "poche" */ "../pages/poche/PocheShell"));
export const PocheHome = differee(() => import(/* webpackChunkName: "poche" */ "../pages/poche/PocheHome"));
export const PocheDictate = differee(() => import(/* webpackChunkName: "poche" */ "../pages/poche/PocheDictate"));
export const PocheVerify = differee(() => import(/* webpackChunkName: "poche" */ "../pages/poche/PocheVerify"));
export const PocheFind = differee(() => import(/* webpackChunkName: "poche" */ "../pages/poche/PocheFind"));

// ── compte (accès de l'équipe, sans connexion) ───────────────────────────
export const LoginPage = differee(() => import(/* webpackChunkName: "compte" */ "../pages/LoginPage"));
export const ForgotPasswordPage = differee(() => import(/* webpackChunkName: "compte" */ "../pages/ForgotPasswordPage"));
export const ResetPasswordPage = differee(() => import(/* webpackChunkName: "compte" */ "../pages/ResetPasswordPage"));
export const InviteAcceptPage = differee(() => import(/* webpackChunkName: "compte" */ "../pages/InviteAcceptPage"));
export const ResponsablePage = differee(() => import(/* webpackChunkName: "compte" */ "../pages/ResponsablePage"));

// ── Préchargement en tâche de fond ───────────────────────────────────────
// Télécharge le code des pages d'équipe AVANT d'y aller : appelé à
// l'ouverture de la page de connexion, le code arrive pendant que le
// responsable tape son mot de passe (sur un Android modeste en 4G, le
// morceau « admin » et ses bibliothèques pèsent ~300 Ko compressés). Seule
// l'équipe ouvre /login : les donateurs ne téléchargent rien de plus.
// Même import() que les pages ci-dessus : le navigateur ne télécharge
// chaque morceau qu'une fois. Échec silencieux : à l'ouverture de la page,
// lazyAvecRecharge retentera (2e essai, puis « Recharger »).
const PRECHARGEMENTS = {
  admin: () => import(/* webpackChunkName: "admin" */ "../components/layout/CadreAdmin"),
  poche: () => import(/* webpackChunkName: "poche" */ "../pages/poche/PocheShell"),
};

/** Précharge le code de la page où mènera la connexion (Poche ou administration). */
export function prechargerApresConnexion(adresse) {
  const chemin = typeof adresse === "string" ? adresse : "";
  const morceau = chemin === "/poche" || chemin.startsWith("/poche/") ? "poche" : "admin";
  try {
    PRECHARGEMENTS[morceau]().catch(() => {});
  } catch {
    /* jamais bloquant */
  }
}
